/**
 * ConnectorSelection — the dedicated "which socket did you plug into?"
 * step.
 *
 * ── WHERE THIS SITS ──────────────────────────────────────────
 *
 * Charger profile → [Get Started] → (this step, conditionally) → the
 * existing flow. EvProfile resolves the routing at tap time:
 *
 *   0 usable connectors → blocked on the profile, never reaches here
 *   1 usable connector  → auto-selected, this step is SKIPPED
 *   2+ usable           → this step
 *
 * So the common single-connector journey is untouched, and this screen
 * only ever renders when the driver genuinely has a choice to make.
 *
 * ── WHAT IT MUST NOT DO ──────────────────────────────────────
 *
 * It does not create a session. Session creation happens later, at the
 * existing `ensureCustomerSession` boundary, exactly as before. This
 * step only records WHICH connector the driver picked, so browser-back
 * from here can never leave a stray session behind.
 *
 * Every status string shown here comes from `describeConnector()`. Raw
 * OCPP vocabulary ("SuspendedEVSE", "ConnectorLockFailure") is never
 * rendered, and it is NOT carried on the element either. It used to sit
 * on `data-raw-status` / `data-raw-error-code` "for diagnostics", but a
 * data attribute on a customer-facing page is neither a log nor secure —
 * anyone with devtools could read exactly how the hardware was failing.
 * The raw code lives on the API side, in operational diagnostics that
 * never enter a response body.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { EvHeader, ActionButtonWrapper, Notice } from '../../index.js';
import { useSession } from '../../../context/SessionContext.jsx';
import { describeConnectors, selectableConnectors } from '../../../utils/connectorStatus.js';
import './ConnectorSelection.less';

const ConnectorSelection = ({ handleIncreaseStep }) => {
  const sessionContext = useSession();
  const session = sessionContext.state;

  const connectors = useMemo(
    () => describeConnectors(session.stationData?.station_status),
    [session.stationData],
  );

  const selectedConnectorId = session.connectorId;
  const selected = connectors.find((c) => c.connectorId === selectedConnectorId) || null;

  // A selection is only valid while the connector is still selectable.
  // `stationData` is refreshed by the rest of the flow, so a connector
  // taken by another driver between render and confirmation lands here.
  const selectionIsStale = selectedConnectorId != null && (!selected || !selected.selectable);

  // Drop a selection that went stale. We clear it rather than silently
  // moving the driver to a different socket — starting the WRONG
  // physical connector is the failure mode this whole step exists to
  // prevent. The driver re-picks explicitly.
  useEffect(() => {
    if (selectionIsStale) {
      console.warn('[ConnectorSelection] clearing stale selection', {
        connectorId: selectedConnectorId,
        rawStatus: selected?.raw?.status ?? null,
        rawErrorCode: selected?.raw?.errorCode ?? null,
      });
      sessionContext.setConnectorId(null);
    }
  }, [selectionIsStale, selectedConnectorId, selected, sessionContext]);

  const handleSelect = useCallback(
    (connector) => {
      if (!connector.selectable) return;
      sessionContext.setConnectorId(connector.connectorId);
    },
    [sessionContext],
  );

  const hasValidSelection = selected != null && selected.selectable;

  const actions = [
    {
      id: 'confirm-connector',
      label: 'Continue',
      type: 'btn-dark',
      disabled: !hasValidSelection,
    },
  ];

  const handleConfirm = (actionId) => {
    if (actionId !== 'confirm-connector') return;
    // Guard the transition as well as the button: a disabled control is
    // a UI affordance, not a security boundary.
    if (!hasValidSelection) return;
    handleIncreaseStep();
  };

  const usableCount = selectableConnectors(connectors).length;

  return (
    <div className="ev-content-wrapper">
      <EvHeader headerAction={{ label: 'Select connector', action: null }} />

      <div className="cs-wrapper">
        <h2 className="cs-title">Select connector</h2>
        <p className="cs-subtitle">Tap the connector you plugged into.</p>

        {selectionIsStale && (
          <Notice variant="warning" role="status" className="cs-stale-notice">
            That connector is no longer available. Please choose another one.
          </Notice>
        )}

        {usableCount === 0 && (
          <Notice variant="warning" role="status" className="cs-stale-notice">
            No connector on this charger is available right now.
          </Notice>
        )}

        <ul
          className="cs-list"
          role="radiogroup"
          aria-label="Available connectors"
          data-testid="connector-selection-list"
        >
          {connectors.map((connector) => {
            const isSelected = connector.connectorId === selectedConnectorId;
            const rowClass = [
              'cs-row',
              connector.selectable ? 'cs-row--selectable' : 'cs-row--disabled',
              isSelected ? 'cs-row--selected' : '',
            ]
              .filter(Boolean)
              .join(' ');

            // The accessible name carries label + status + any fault, so
            // a screen-reader user gets the same information a sighted
            // user reads off the card — status is never colour-only.
            const accessibleName = [
              connector.label,
              connector.statusLabel,
              connector.note,
            ]
              .filter(Boolean)
              .join(', ');

            return (
              <li key={connector.connectorId} className={rowClass}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={accessibleName}
                  disabled={!connector.selectable}
                  className="cs-row-btn"
                  onClick={() => handleSelect(connector)}
                  data-testid={`connector-option-${connector.connectorId}`}
                  // Diagnostics only — never rendered as text.
                >
                  <span className="cs-row-main">
                    <span className="cs-row-label">{connector.label}</span>
                    {session.stationMeta?.connectorType && (
                      <span className="cs-row-type">{session.stationMeta.connectorType}</span>
                    )}
                  </span>

                  <span className="cs-row-meta">
                    <span className="cs-row-status" data-ui-state={connector.uiState}>
                      {connector.statusLabel}
                    </span>
                    {connector.note && <span className="cs-row-note">{connector.note}</span>}
                  </span>

                  <span className="cs-row-check" aria-hidden="true">
                    {isSelected ? '✓' : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <ActionButtonWrapper actions={actions} onClickAction={handleConfirm} />
    </div>
  );
};

export default ConnectorSelection;
